import Vue from 'vue';
import makeStoreDebugAccessible from '../util/storeDebug';
import BookmarkApiService from '../components/ui/user-settings/bookmark/BookmarkApiService';
import ContinueApiService from '../components/ui/user-settings/continue/ContinueApiService';
import MostRecentlyUsedApiService from '../components/ui/user-settings/most-recently-used/MostRecentlyUsedApiService';
import {
  UpsertBookmark,
  UpsertContinueDto,
  Book
} from '../types/userSettingServiceTypes';
import { Site } from '../types/initialData';

export interface Bookmark {
  pageId: number;
  contentId: number;
  createdTimeStamp?: number;
  isbn?: string;
  externalUserId?: string;
  myaccountId?: string;
  userId?: string;
  pageTitle?: string;
  breadCrumb?: string;
  debugHostName?: string;
}

export interface Continue {
  pageId: number;
  createdTimeStamp?: number;
  isbn?: string;
  externalUserId?: string;
  myaccountId?: string;
  userId?: string;
  debugHostName?: string;
}

interface IUserSettingsState {
  bookmarks: Bookmark[];
  continue: Continue | null;
  mostRecentlyUsedBooks: Book[];
}

const state: IUserSettingsState = Vue.observable({
  bookmarks: [],
  continue: null,
  mostRecentlyUsedBooks: []
});

interface IUserSettingsStore {
  readonly bookmarks: Bookmark[];
  fetchBookmarksForIsbn(site: Site): Promise<void>;
  createOrUpdateBookmark(bookmark: Bookmark, site: Site): Promise<void>;
  deleteBookmark(bookmark: Bookmark, site: Site): Promise<void>;

  readonly continue_: Continue | null;
  fetchContinueForIsbn(site: Site): Promise<void>;
  changeOrCreateContinue(cont: Continue, site: Site): Promise<void>;
  deleteContinue(site: Site): Promise<void>;

  readonly mostRecentlyUsedBooks: Book[];
  fetchMostRecentlyUsedBooks(site: Site): Promise<void>;
  addMostRecentlyUsedBook(book: Book, site: Site): Promise<void>;
  clearMostRecentlyUsedBooks(site: Site): Promise<void>;
}

class UserSettingsStore implements IUserSettingsStore {
  constructor() {
    makeStoreDebugAccessible('userSettingsStore', state);
  }

  async fetchMostRecentlyUsedBooks(site: Site): Promise<void> {
    state.mostRecentlyUsedBooks = await MostRecentlyUsedApiService.getList(
      site
    );
  }

  async addMostRecentlyUsedBook(book: Book, site: Site): Promise<void> {
    state.mostRecentlyUsedBooks = await MostRecentlyUsedApiService.addMru(
      book,
      site
    );
  }

  async clearMostRecentlyUsedBooks(site: Site): Promise<void> {
    await MostRecentlyUsedApiService.clearList(site);
    state.mostRecentlyUsedBooks = [];
  }

  async fetchBookmarksForIsbn(site: Site) {
    const bookmarks = await BookmarkApiService.getBookmarksForIsbn(site);
    state.bookmarks =
      bookmarks?.map((response) => {
        return { pageId: response.pageId, contentId: response.contentId };
      }) || [];
  }

  async fetchContinueForIsbn(site: Site) {
    try {
      const response = await ContinueApiService.getContinueForIsbn(site);
      state.continue = { pageId: response.pageId };
    } catch (e: any) {
      if (e.response!.status === 404) {
        state.continue = null;
      } else {
        throw e;
      }
    }
  }

  async changeOrCreateContinue(cont: Continue, site: Site) {
    const request: UpsertContinueDto = {
      pageId: cont.pageId,
      isbn: cont.isbn,
      externalUserId: cont.externalUserId,
      myaccountId: cont.myaccountId,
      userId: cont.userId,
      debugHostName: cont.debugHostName
    };
    state.continue = await ContinueApiService.createOrUpdateContinue(
      request,
      site
    );
  }

  async createOrUpdateBookmark(bookmark: Bookmark, site: Site) {
    const request: UpsertBookmark = {
      pageId: bookmark.pageId,
      contentId: bookmark.contentId,
      isbn: bookmark.isbn,
      externalUserId: bookmark.externalUserId,
      myaccountId: bookmark.myaccountId,
      userId: bookmark.userId,
      pageTitle: bookmark.pageTitle,
      breadCrumb: bookmark.breadCrumb,
      debugHostName: bookmark.debugHostName
    };
    const response: Bookmark = await BookmarkApiService.createOrUpdateBookmark(
      request,
      site
    );

    const bookmarkIdx = state.bookmarks.findIndex(
      (mark) =>
        mark.contentId === bookmark.contentId && mark.pageId === bookmark.pageId
    );
    if (bookmarkIdx !== -1) {
      // eslint-disable-next-line import/no-named-as-default-member
      Vue.set(state.bookmarks, bookmarkIdx, response);
    } else {
      state.bookmarks.push(response);
    }
  }

  async deleteContinue(site: Site) {
    await ContinueApiService.deleteContinue(site);
    state.continue = null;
  }

  async deleteBookmark(bookmark: Bookmark, site: Site) {
    await BookmarkApiService.deleteBookmark(
      bookmark.pageId,
      bookmark.contentId,
      site
    );

    const bookmarkIdx = state.bookmarks.findIndex(
      (mark) =>
        mark.contentId === bookmark.contentId && mark.pageId === bookmark.pageId
    );
    if (bookmarkIdx !== -1) {
      state.bookmarks.splice(bookmarkIdx, 1);
    }
  }

  get bookmarks() {
    return state.bookmarks;
  }

  get continue_() {
    return state.continue;
  }

  get mostRecentlyUsedBooks() {
    return state.mostRecentlyUsedBooks;
  }
}

export default new UserSettingsStore();
